<template>
  <video ref="videoRef" class="video-js" playsinline></video>
  <VideoPlayerControls
    :controls="isControlsVisible"
    :playing="isPlaying"
    :muted="isMuted"
    @play="togglePlay"
    @mute="toggleMuted"
  />
</template>

<script setup lang="ts">
import videojs from 'video.js'
import type Player from 'video.js/dist/types/player'
import 'video.js/dist/video-js.css'

export interface VideoPlayerProps {
  options?: object
}

const props = defineProps<VideoPlayerProps>()
const emit = defineEmits([
  'ready',
  'play',
  'pause',
  'ended',
  'fullscreenchange',
  'useractive',
  'userinactive'
])

const videoRef = ref<HTMLVideoElement | null>(null)
const player = ref<Player | null>(null)
defineExpose({
  player
})

const isControlsVisible = ref(true)
const isPlaying = ref(false)
const isMuted = ref(props.options?.muted)

const togglePlay = () => {
  if (player.value) {
    if (player.value.paused()) {
      player.value.play()
    } else {
      player.value.pause()
    }
  }
}

const toggleMuted = () => {
  if (player.value) {
    if (player.value.muted()) {
      player.value.muted(false)
      isMuted.value = false
    } else {
      player.value.muted(true)
      isMuted.value = true
    }
  }
}

onMounted(() => {
  if (videoRef.value) {
    player.value = videojs(videoRef.value, props.options, () => {
      player?.value!.log('onPlayerReady', player.value)
      player.value?.play()
    })

    player.value.on('play', () => {
      emit('play')
      isPlaying.value = true
    })

    player.value.on('pause', () => {
      emit('pause')
      isPlaying.value = false
    })

    player.value.on('ended', () => {
      emit('ended')
    })

    player.value.on('useractive', () => {
      emit('useractive')
      isControlsVisible.value = true
    })

    player.value.on('userinactive', () => {
      emit('userinactive')
      if (!isPlaying.value) {
        return
      }
      isControlsVisible.value = false
    })

    player.value.on('fullscreenchange', () => {
      emit('fullscreenchange')
    })
  }
})

onBeforeUnmount(() => {
  if (player.value) {
    player?.value!.dispose()
  }
})
</script>

<style lang="postcss">
.video-js {
  @apply !flex h-full items-center justify-center text-grey;

  .vjs-tech {
    @apply static h-auto w-fit object-cover;

    @screen lg {
      @apply h-4/5;
    }
  }

  .vjs-control-bar {
    @apply absolute bottom-7 flex h-fit flex-col gap-6 bg-transparent lg:bottom-8 lg:w-[calc(100%-96px)] lg:flex-row lg:items-center lg:gap-0;
  }

  .vjs-progress-control .vjs-progress-holder {
    @apply mx-4 h-[0.2em] bg-grey lg:mb-0;
  }

  .vjs-progress-control {
    @apply w-full lg:order-2;

    .vjs-play-progress {
      @apply left-10 bg-transparent;

      &::before {
        @apply -top-[2px] flex w-20 items-center justify-center text-[18px];
        background: radial-gradient(circle, black 33%, transparent 67%);
      }
    }
  }

  .vjs-load-progress {
    @apply bg-grey;

    div {
      @apply bg-grey;
    }
  }

  .vjs-remaining-time {
    @apply flex items-center pl-4 pr-0 font-mono text-base lg:order-1 lg:pl-8;
  }
}
</style>
